/* eslint-disable */
import { Box } from "@mui/material";
import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import React from "react";

import { Bill } from "api";

import {
  EMPTY_CELL_VALUE,
  Tooltip,
} from "components";

import { TABLE_HEADERS } from "consts";

import { theme } from "providers";

import {
  getDotColor,
  getFromLocalStorage,
  getStatusLabel,
  getUTCDateFormat,
  isNullOrUndefined,
  toUSNumber,
} from "utils";

// eslint-disable-next-line
import { PreviewUtilityBill } from "./preview-utility-bill";
import { DATA_ENTRIES_STATUS_PRIORITY_MAP } from "../../consts";

export const DOCUMENT_UTILITY_BILLS_COLUMNS: ColumnDef<Bill>[] = [
  {
    header: () => <Tooltip title="Utility type"><Box>Utility type</Box></Tooltip>,
    accessorKey: "type",
    cell: ({ getValue }) => {
      const type = getValue() as string | null;
      if (!type) {
        return EMPTY_CELL_VALUE;
      }
      return <Tooltip title={type}>
        <Box>{type}</Box>
      </Tooltip>;
    },
    minSize: 70,
  },
  {
    header: () => <Tooltip title="Start Date"><Box>Start Date</Box></Tooltip>,
    accessorKey: "consumptionPeriodFrom",
    cell: ({ getValue, row }) => {
      const date: string = getValue() as string;
      const value = date && dayjs(date).utc().format(getUTCDateFormat());
      if (isNullOrUndefined(value)) {
        return EMPTY_CELL_VALUE;
      }
      const selectedRow: any = getFromLocalStorage("selectedRow");
      let style = {};
      if (selectedRow) {
        const rowSelected: any = JSON.parse(selectedRow);
        const rowDisplay: any = row.original;
        if (rowSelected.id === rowDisplay.id) {
          style = { backgroundColor: theme.palette.grey[300], color: "black" };
        }
      }

      return <Tooltip title={value}>
        <Box sx={{ textAlign: "center", ...style }}>{`${value}`}</Box>
      </Tooltip>;
    },
    minSize: 90,
  },
  {
    header: () => <Tooltip title="End Date"><Box>End Date</Box></Tooltip>,
    accessorKey: "consumptionPeriodTo",
    cell: ({ getValue, row }) => {
      const date: string = getValue() as string;
      const value = date && dayjs(date).utc().format(getUTCDateFormat());
      if (isNullOrUndefined(value)) {
        return EMPTY_CELL_VALUE;
      }
      const selectedRow: any = getFromLocalStorage("selectedRow");
      let style = {};
      if (selectedRow) {
        const rowSelected: any = JSON.parse(selectedRow);
        const rowDisplay: any = row.original;
        if (rowSelected.id === rowDisplay.id) {
          style = { backgroundColor: theme.palette.grey[300], color: "black" };
        }
      }
      return <Tooltip title={value}>
        <Box sx={{ textAlign: "center", ...style }}>{`${value}`}</Box>
      </Tooltip>;
    },
    minSize: 90,
  },
  {
    header: () => <Tooltip title="Provider"><Box>Provider</Box></Tooltip>,
    accessorKey: "provider",
    cell: ({ getValue, row }) => {
      const value = getValue() as string;
      if (isNullOrUndefined(value)) {
        return EMPTY_CELL_VALUE;
      }
      const selectedRow: any = getFromLocalStorage("selectedRow");
      let style = {};
      if (selectedRow) {
        const rowSelected: any = JSON.parse(selectedRow);
        const rowDisplay: any = row.original;
        if (rowSelected.id === rowDisplay.id) {
          style = { backgroundColor: theme.palette.grey[300], color: "black" };
        }
      }
      return <Tooltip title={value}>
        <Box sx={{ textAlign: "center", ...style }}>{`${value}`}</Box>
      </Tooltip>;
    },
    minSize: 80,
  },
  {
    header: () => <Tooltip title={TABLE_HEADERS.accountNumber}><Box>{TABLE_HEADERS.accountNumber}</Box></Tooltip>,
    accessorKey: "accountNumber",
    cell: ({ getValue, row }) => {
      const value = getValue() as string;
      if (isNullOrUndefined(value)) {
        return EMPTY_CELL_VALUE;
      }
      const selectedRow: any = getFromLocalStorage("selectedRow");
      let style = {};
      if (selectedRow) {
        const rowSelected: any = JSON.parse(selectedRow);
        const rowDisplay: any = row.original;
        if (rowSelected.id === rowDisplay.id) {
          style = { backgroundColor: theme.palette.grey[300], color: "black" };
        }
      }
      return <Tooltip title={value}>
        <Box sx={{ textAlign: "center", ...style }}>{`${value}`}</Box>
      </Tooltip>;
    },
    minSize: 70,
  },
  {
    header: () => <Tooltip title={TABLE_HEADERS.meterNumber}><Box>{TABLE_HEADERS.meterNumber}</Box></Tooltip>,
    accessorKey: "meterNumber",
    cell: ({ getValue, row }) => {
      const value = getValue() as string;
      if (isNullOrUndefined(value)) {
        return EMPTY_CELL_VALUE;
      }
      const selectedRow: any = getFromLocalStorage("selectedRow");
      let style = {};
      if (selectedRow) {
        const rowSelected: any = JSON.parse(selectedRow);
        const rowDisplay: any = row.original;
        if (rowSelected.id === rowDisplay.id) {
          style = { backgroundColor: theme.palette.grey[300], color: "black" };
        }
      }
      return <Tooltip title={value}>
        <Box sx={{ textAlign: "center", ...style }}>{`${value}`}</Box>
      </Tooltip>;
    },
    minSize: 85,
  },
  {
    header: () => <Tooltip title="Usage"><Box>Usage</Box></Tooltip>,
    accessorKey: "measuredUsageValue",
    minSize: 55,
    cell: ({ getValue, row }) => {
      const value = getValue();
      if (isNullOrUndefined(value)) {
        return EMPTY_CELL_VALUE;
      }
      const selectedRow: any = getFromLocalStorage("selectedRow");
      let style = {};
      if (selectedRow) {
        const rowSelected: any = JSON.parse(selectedRow);
        const rowDisplay: any = row.original;
        if (rowSelected.id === rowDisplay.id) {
          style = { backgroundColor: theme.palette.grey[300], color: "black" };
        }
      }
      return (
        <Tooltip title={`${toUSNumber(value as number)}${row.original?.measuredUsageUnit === null ? "" : row.original?.measuredUsageUnit}`}>
          <Box sx={{ textAlign: "center", ...style }}>
            {`${toUSNumber(value as number)} ${row.original?.measuredUsageUnit === null ? "" : row.original?.measuredUsageUnit}`}</Box>
        </Tooltip>
      );
    },
  },
  {
    header: () => <Tooltip title="Cost"><Box>Cost</Box></Tooltip>,
    accessorKey: "amount",
    minSize: 50,
    cell: ({ getValue, row }) => {
      const value = getValue() as number;
      if (isNullOrUndefined(value)) {
        return EMPTY_CELL_VALUE;
      }
      const selectedRow: any = getFromLocalStorage("selectedRow");
      let style = {};
      if (selectedRow) {
        const rowSelected: any = JSON.parse(selectedRow);
        const rowDisplay: any = row.original;
        if (rowSelected.id === rowDisplay.id) {
          style = { backgroundColor: theme.palette.grey[300], color: "black" };
        }
      }
      return (
        <Tooltip title={`${toUSNumber(value)} ${row.original?.currency === null ? "" : row.original?.currency}`}>
          <Box sx={{ textAlign: "right", ...style }}>
            {`${toUSNumber(value)} ${row.original?.currency === null ? "" : row.original?.currency}`}</Box>
        </Tooltip>
      );
    },
  },
  {
    header: () => <Tooltip title="Status"><Box>Status</Box></Tooltip>,
    accessorKey: "statusFilter",
    cell: ({ cell, row }: any) => {
      const statusFilter: string = cell?.getValue();
      const missingFields = row?.original?.missingFields ?? [];
      const priority = DATA_ENTRIES_STATUS_PRIORITY_MAP[statusFilter] ?? 9;

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            paddingLeft: "4px",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <span style={{ position: "relative" }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 5 5"
              fill={getDotColor(priority)}
              style={{ marginRight: "9px" }}
            >
              <circle cx="2.5" cy="2.5" r="2.5" />
            </svg>
            {getStatusLabel(statusFilter, missingFields)}
          </span>
        </Box>
      );
    },
    minSize: 150,
  },
  {
    id: "preview",
    header: "",
    accessorKey: "id",
    cell: ({ getValue, row }) => (
      <PreviewUtilityBill
        id={getValue() as string}
        rowData={JSON.stringify(row?.original)}
      />
    ),
    minSize: 60,
  },
];
